import { HTMLAttributes, ReactNode } from "react"
import { TextFieldProps } from "@material-ui/core"
import { ISearchQuery } from "./dynamicFormFieldsTypes"
import { DeliveryPayloadType } from "../../Types/MeetingPlanning/deliveryForm"
import { DateTime } from "luxon"

export enum ValidationType {
    IS_EMPTY_LIST = "IS_EMPTY_LIST",
    REQUIRED = "REQUIRED",
    IS_TRUE = "IS_TRUE",
    MAX_LENGTH = "MAX_LENGTH",
    DELIVERY_LOCATION_FIELDS_CONDITIONAL = "DELIVERY_LOCATION_FIELDS_CONDITIONAL",
    EMAIL = "EMAIL",
    REGEX = "REGEX",
    MAX = "MAX",
    MIN = "MIN"
}

type ArrayValidation = Readonly<{
    type: ValidationType.IS_EMPTY_LIST
    message: string
}>

type RequiredValidation = Readonly<{
    type: ValidationType.REQUIRED
    value?: number
    message: string
}>

type IsTrueValidation = Readonly<{
    type: ValidationType.IS_TRUE
    message: string
}>

type MaxLengthValidation = Readonly<{
    type: ValidationType.MAX_LENGTH
    value: number
    message: string
}>

type DeliveryLocationFieldsConditionalValidation = Readonly<{
    type: ValidationType.DELIVERY_LOCATION_FIELDS_CONDITIONAL
    when: {
        field: string
        type: 'string' | 'boolean' | 'array'
        is: string | boolean | []
        hasOtherDeliveryLocation?: boolean
        additionalFieldName?: string
    }
    message: string
}>

type EmailValidation = Readonly<{
    type: ValidationType.EMAIL
    message: string
}>

type RegexValidation = Readonly<{
    type: ValidationType.REGEX
    regex: string
    message: string
}>

type MaxValidation = Readonly<{
    type: ValidationType.MAX
    value: number
    message: string
}>

type MinValidation = Readonly<{
    type: ValidationType.MIN
    value: number
    message: string
}>

export type ValidationRule =
    | ArrayValidation
    | RequiredValidation
    | IsTrueValidation
    | MaxLengthValidation
    | DeliveryLocationFieldsConditionalValidation
    | EmailValidation
    | RegexValidation
    | MaxValidation
    | MinValidation

export enum InputType {
    TEXT = "TEXT",
    TEXTAREA = "TEXTAREA",
    CHECKBOX = "CHECKBOX",
    DELIVERY_SELECT = "DELIVERY_SELECT",
    SELECT = "SELECT",
    DYNAMIC_AUTOCOMPLETE = "DYNAMIC_AUTOCOMPLETE",
    EXTRA_SERVICES = "EXTRA_SERVICES",
    ACCOUNTING_DIMENSION_TEXT_FIELD = "ACCOUNTING_DIMENSION_TEXT_FIELD",
    OTHER_DELIVERY_COMPONENT = "OTHER_DELIVERY_COMPONENT",
    BUTTON = "BUTTON",
    NUMBER_SELECT = "NUMBER_SELECT"
}

export type FieldInitialValue = string | number | boolean | Array<{ label: string, value: string | number }>

type BaseInput = Readonly<Pick<TextFieldProps, "placeholder" | "disabled"> & {
    name: string
    label?: ReactNode | string
    title?: string
    placeholder?: string
    helperText?: string
    valueType?: "string" | "number" | "boolean" | "array"
    initialValue?: FieldInitialValue
    key?: string
    searchQuery?: ISearchQuery
    required?: boolean
    isMultiline?: boolean
    isActive?: boolean
    validations: ValidationRule[]
}>

type TextInput = BaseInput & Readonly<Pick<TextFieldProps, "autoComplete" | "autoFocus"> & {
    type: InputType.TEXT
    variant?: 'standard' | 'filled' | 'outlined' | undefined
    inputType?: TextFieldProps["type"]
    inputMode?: HTMLAttributes<"input">["inputMode"]
}>

type AccountingDimensionTextInput = BaseInput & Readonly<Pick<TextFieldProps, "autoComplete" | "autoFocus"> & {
    type: InputType.ACCOUNTING_DIMENSION_TEXT_FIELD
    variant?: 'standard' | 'filled' | 'outlined' | undefined
    inputType?: TextFieldProps["type"]
    inputMode?: HTMLAttributes<"input">["inputMode"]
    dimensionId?: number | null;
}>

type SelectInput = BaseInput & {
    type: InputType.SELECT
    options: Array<{ value: string | number; label: string }>
}

type TextareaInput = BaseInput & {
    type: InputType.TEXTAREA
}

type DeliverySelectInput = BaseInput & {
    type: InputType.DELIVERY_SELECT
    options: Array<{ value: string | number; label: string }>
    onDeliveryChange?: (deliveryType: DeliveryPayloadType) => void
}

type NumberSelectInput = BaseInput & {
    type: InputType.NUMBER_SELECT
}

interface CheckboxInput extends BaseInput {
    type: InputType.CHECKBOX
}

interface ExtraServicesInput extends BaseInput {
    type: InputType.EXTRA_SERVICES
}

interface DeliveryComponentInput extends BaseInput {
    type: InputType.OTHER_DELIVERY_COMPONENT
}

export type DynamicAutocompleteInput = BaseInput & {
    type: InputType.DYNAMIC_AUTOCOMPLETE
    mode?: IConfigMode;
    dimensionId?: number | null;
    options: Array<{ label: string, value: string | number }>
}


type DynamicButtonInput = BaseInput & {
    type: InputType.BUTTON
    action: () => void
}

export type FormInputField =
    | TextInput
    | TextareaInput
    | CheckboxInput
    | SelectInput
    | DeliverySelectInput
    | NumberSelectInput
    | DynamicAutocompleteInput
    | AccountingDimensionTextInput
    | ExtraServicesInput
    | DeliveryComponentInput
    | DynamicButtonInput

export enum IConfigMode {
    LOAD_ON_OPEN = 'LOAD_ON_OPEN',
    SEARCH_AS_YOU_TYPE = 'SEARCH_AS_YOU_TYPE'
}
